<template>
    <div
        id="banner-a"
        class="relative h-svh w-full overflow-hidden"
        :data-theme="theme"
        :data-mode="borderMode"
        :class="{
            'p-3 surface-background': banner?.bannerA_ShowBorder
        }"
    >

        <!-- carousel or video -->
        <component
            :is="backgroundTypeComponent"
            data-mode="color-invert"
            :banner="banner"
            :class="{
                'h-full overflow-hidden radius-card-lg': banner?.bannerA_ShowBorder
            }"
        />

        <div 
            data-mode="color-invert" 
            class="absolute inset-0 z-10 px-3 py-32"
            :class="{
                'px-6': banner?.bannerA_ShowBorder
            }"    
        >
            <div class="container-extra-wide mx-auto relative h-full">
                <div
                    class="flex w-full h-full px-4 md:px-0"
                    :class="{
                        'items-center': ['left', 'center', 'right'].includes(banner.bannerA_Alignment),
                        'justify-end': ['top-right', 'right', 'bottom-right'].includes(banner.bannerA_Alignment),
                        'justify-center': ['top', 'center', 'bottom'].includes(banner.bannerA_Alignment),
                        'items-end': ['bottom-left', 'bottom', 'bottom-right'].includes(banner.bannerA_Alignment),
                    }"
                >

                    <div 
                        class="flex flex-col"
                        :class="{
                            'max-w-[576px]': banner.bannerA_ContentContainerWidth === 'narrow',
                            'max-w-[768px]': banner.bannerA_ContentContainerWidth === 'wide',
                            'text-center items-center': ['center', 'top', 'bottom'].includes(banner.bannerA_Alignment),
                            'text-right items-end': ['right', 'top-right', 'bottom-right'].includes(banner.bannerA_Alignment),
                        }">
                        <RichText
                            :text="banner.bannerA_Content"
                            class="mb-6"
                        />

                        <div
                            v-if="banner.bannerA_Buttons.length"
                            class="flex flex-col md:flex-row items-center gap-4 w-fit"
                        >
                            <NuxtLink
                                v-for="(button, buttonIndex) in banner.bannerA_Buttons"
                                :key="buttonIndex"
                                :to="button.url"
                            >
                                <DButton
                                    size="lg"
                                    :theme="buttonTheme(button.buttonStyle)"
                                    :right-icon="button.icon?.name"
                                    :icon-set="button.icon?.set"
                                    :icon-type="button.icon?.type"
                                >
                                    {{ button.text }}
                                </DButton>
                            </NuxtLink>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
    import { DButton } from '@digistorm/spark'

    const { buttonTheme } = useButtonTheme()
    const { bannerHasBorder } = storeToRefs(useSiteStore())

    const props = defineProps({
        entry: Object
    })

    const banner = computed(() => {
        return first(props.entry?.banner)
    })

    const backgroundType = computed(() => {
        const bannerVideos = get(banner.value, 'bannerA_Video', null)
        return bannerVideos.length > 0 ? 'video': 'carousel'
    })

    const backgroundTypeComponent = defineAsyncComponent(() =>
        import(`./${backgroundType.value}.async.vue`)
    )

    const theme = computed(() => {
        return get(banner.value, 'bannerA_ColorTheme')
    })

    const borderMode = computed(() => {
        return get(banner.value, 'bannerA_BorderColorPalette')
    })

    onMounted(() => {
        // store border value to be used in nav components
        bannerHasBorder.value = get(banner.value, 'bannerA_ShowBorder', false)
    })

</script>

<style lang="scss">
    #banner-a {
        .text-heading, .text-subheading, .text-lead-text {
            @apply mb-4;
        }
        .text-lead-text {
            @apply mt-0;
        }
    }
</style>